import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Seo } from "@/site/seo";
import { findArticle, ALL_ARTICLES, isPublished, pillar, CTA_MAP, AUTHOR } from "@/site/articles";
import { fmtDate } from "@/site/api";
import { SITE } from "@/site/content";
import { track } from "@/site/analytics";
import NotFound from "@/site/pages/NotFound";
import Mark from "@/site/components/Mark";
import { ShareRow } from "@/site/components/Social";

// Internal links stay client-side; external ones open safely.
const A = ({ href = "", children }) => {
  if (href.startsWith("/") && !href.startsWith("//")) return <Link to={href}>{children}</Link>;
  return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
};

export default function ArticlePage() {
  const { slug } = useParams();
  const post = findArticle(slug);

  useEffect(() => { if (post) track("article_view", { slug: post.slug }); window.scrollTo(0, 0); }, [post]);

  if (!post) return <NotFound />;

  const hub = pillar();
  const related = post.related.map((s) => ALL_ARTICLES.find((a) => a.slug === s)).filter((a) => a && isPublished(a) && a.slug !== post.slug);
  const cta = CTA_MAP[post.cta] || CTA_MAP.pilot;
  const url = `${SITE.url}/blog/${post.slug}`;
  const ld = {
    "@context": "https://schema.org", "@type": "Article", headline: post.title, description: post.description,
    datePublished: post.date, dateModified: post.date, mainEntityOfPage: url, wordCount: post.words, keywords: [post.keyword, ...post.tags].filter(Boolean).join(", "),
    author: { "@type": "Organization", name: AUTHOR, url: SITE.url },
    publisher: { "@type": "Organization", name: SITE.name, url: SITE.url, logo: { "@type": "ImageObject", url: `${SITE.url}/og/og-default.jpg` } },
    image: `${SITE.url}/og/og-default.jpg`,
  };

  return (
    <>
      <Seo title={post.title} description={post.description} path={`/blog/${post.slug}`} type="article" jsonLd={ld} />
      <article className="section" data-testid="article">
        <div className="wrap" style={{ maxWidth: 760 }}>
          <nav aria-label="Breadcrumb" className="crumbs"><Link to="/blog">Blog</Link><span aria-hidden="true">/</span><span>{post.tags[0] || "Essay"}</span></nav>
          <header className="article__head">
            <h1 className="article__title" data-testid="article-title">{post.title}</h1>
            <p className="article__desc">{post.description}</p>
            <div className="article__by" data-testid="article-byline">
              <span className="article__avatar" aria-hidden="true"><Mark size={18} /></span>
              <span><b>{AUTHOR}</b> · {fmtDate(post.date)} · {post.minutes} min read</span>
            </div>
            <ShareRow url={url} title={post.title} />
          </header>

          {hub && !post.pillar && (
            <div className="article__series" data-testid="article-series">
              Part of a series on <Link to={`/blog/${hub.slug}`}>account whitespace</Link> — the pillar piece is the place to start.
            </div>
          )}

          <div className="prose" data-testid="article-body">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: A }}>{post.body}</ReactMarkdown>
          </div>

          <aside className="article__cta" data-testid="article-cta">
            <div>
              <div className="eyebrow">If this was useful</div>
              <div className="article__cta-note">{cta.note}</div>
            </div>
            {cta.to.startsWith("/#") ? <a className="btn btn--primary" href={cta.to} onClick={() => track("article_cta", { slug: post.slug, cta: post.cta })}>{cta.label} →</a>
              : <Link className="btn btn--primary" to={cta.to} onClick={() => track("article_cta", { slug: post.slug, cta: post.cta })}>{cta.label} →</Link>}
          </aside>

          {related.length > 0 && (
            <section className="article__related" data-testid="article-related">
              <div className="eyebrow">Read next</div>
              <div className="post-list">
                {related.map((r) => (
                  <Link key={r.slug} to={`/blog/${r.slug}`} className="post post--sm" data-testid={`related-${r.slug}`}>
                    <div className="post__meta"><span>{fmtDate(r.date)}</span><span>·</span><span>{r.minutes} min</span></div>
                    <h3 className="post__title" style={{ fontSize: 17 }}>{r.title}</h3>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </article>
    </>
  );
}
